// NIP-46 (Nostr Connect / bunker) client.
//
// Two pairing flows are supported:
//
//   1. nostrconnect:// (client-initiated, QR-scan friendly)
//      The app generates an ephemeral client keypair, advertises a
//      connect URI, and waits for the signer (clave.casa, nsec.app,
//      Amber, nsecBunker, etc.) to dial back over the relay. Used as
//      the default flow on desktop and Android.
//
//   2. bunker:// (signer-initiated, paste-friendly)
//      The signer generates a `bunker://<pubkey>?relay=...&secret=...`
//      URI in advance; the user pastes it into our app; we publish a
//      `connect` request directly to the signer's pubkey. Used as the
//      primary flow on iOS Safari, where the QR-scan flow fails
//      because Safari kills in-flight WebSockets when backgrounded
//      (and pairing requires a tab-switch to clave to scan the QR).
//
//      With the bunker:// paste flow, Safari is never backgrounded
//      during pairing - clave is opened FIRST, generates the URI,
//      then the user pastes into Safari and Safari stays foreground
//      while the connect RPC round-trips.
//
// Scoped perms: see ./perms.ts. We request only get_public_key, sign_event:1,
// and sign_event:30078 so the signer enforces kind allowlist server-side.
//
// History note: commit d2134e4 (2026-06-11) routed connectBunker through
// a Cloudflare Worker long-poll to bypass iOS Safari WebSocket suspension
// for the QR flow. That path is reverted to direct BunkerSigner.fromURI
// because (a) Safari kills the long-poll fetch itself when backgrounded,
// so the proxy doesn't actually help, and (b) bunker:// paste is the
// correct iOS UX per Clave's own compat doc. The worker endpoint at
// `packages/worker/src/routes/auth-bunker.ts` remains deployed but
// unused; we may revive it for a future cross-device path.

import { generateSecretKey, getPublicKey } from 'nostr-tools/pure';
import {
  BunkerSigner,
  createNostrConnectURI,
  parseBunkerInput,
  type BunkerPointer,
} from 'nostr-tools/nip46';
import { SimplePool } from 'nostr-tools/pool';

import { NOSTR_AUTH_PERMS } from './perms.js';
import {
  BunkerConnectError,
  BunkerTimeoutError,
  type AuthResult,
  type NostrSigner,
  type SignedNostrEvent,
  type UnsignedNostrEvent,
} from './types.js';

export interface NostrConnectUri {
  uri: string;
  /** Hex pubkey of the ephemeral client key the signer must dial back to. */
  clientPubkey: string;
  /** Random secret that the signer must echo in its connect response. */
  secret: string;
  /**
   * Primary relay. Equal to {@link relays}[0]. Kept for back-compat with
   * callers that only ever consume one relay (cleanup paths, logs).
   */
  relay: string;
  /**
   * All relays advertised in the URI. The worker-side listener
   * subscribes to every entry. We publish to multiple relays so signers
   * (notably clave.casa) can wake their listener via a relay their
   * infrastructure already watches. Always non-empty.
   */
  relays: readonly [string, ...string[]];
  /** Bytes of the ephemeral client secret key (held in memory only). */
  clientSecretKey: Uint8Array;
}

export interface GenerateConnectUriOpts {
  appName: string;
  /**
   * Single relay shorthand. The URI will advertise this relay only.
   * Prefer {@link GenerateConnectUriOpts.relays} for multi-relay
   * pairing (recommended for clave.casa compat - see ./nip46.ts header).
   * Exactly one of `relay` or `relays` must be set.
   */
  relay?: string;
  /**
   * Full relay set to advertise in the nostrconnect:// URI. Each entry
   * is emitted as a separate `relay=` query param per NIP-46:
   *
   *   nostrconnect://<pk>?relay=<wss-a>&relay=<wss-b>&secret=...
   *
   * Multiple relays widen the dial-back surface so the signer's connect
   * response lands on whichever relay both sides can reach. For clave
   * specifically, include `wss://relay.powr.build` so Clave's relay
   * proxy can wake the iOS NSE via APNs.
   */
  relays?: readonly string[];
  /** Override the default scoped perms string. */
  perms?: string;
}

/**
 * Build a nostrconnect:// URI for the bunker tab.
 *
 * The QR code we display encodes this URI. The remote signer scans it,
 * connects to one of {relays}, and sends a kind-24133 NIP-46 connect
 * request back to {clientPubkey} with the secret echoed in the payload.
 *
 * Per NIP-46 the URI advertises one or more relays via repeated `relay=`
 * query params. nostr-tools `BunkerSigner.fromURI` subscribes to every
 * `relay=` value, so adding extra relays only widens the pairing path -
 * it never breaks the spec-compliant single-relay case.
 */
export function generateConnectUri(opts: GenerateConnectUriOpts): NostrConnectUri {
  const relays = resolveRelays(opts);
  const clientSecretKey = generateSecretKey();
  const clientPubkey = getPublicKey(clientSecretKey);
  const secret = crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  const permsString = opts.perms ?? NOSTR_AUTH_PERMS;
  // nostr-tools createNostrConnectURI expects perms as string[].
  const perms = permsString
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  const uri = createNostrConnectURI({
    clientPubkey,
    relays: [...relays],
    secret,
    perms,
    name: opts.appName,
  });
  return {
    uri,
    clientPubkey,
    secret,
    // resolveRelays() guarantees at least one entry, so [0] is non-null.
    // We type the function's return as `readonly [string, ...string[]]`
    // so callers (and tsc) can rely on this without a runtime assert.
    relay: relays[0],
    relays,
    clientSecretKey,
  };
}

function resolveRelays(
  opts: GenerateConnectUriOpts,
): readonly [string, ...string[]] {
  const fromArray = opts.relays?.filter((r) => r.length > 0) ?? [];
  if (fromArray.length > 0 && opts.relay) {
    // Be strict about the API contract - callers should pick one form.
    // We could dedup the union, but mixing the two is almost always a bug
    // (caller wrote `relay: X, relays: [Y]` and expected both).
    throw new Error(
      'generateConnectUri: pass either `relay` or `relays`, not both',
    );
  }
  if (fromArray.length > 0) {
    // We just checked length > 0, so first is defined. Narrow for tsc.
    const [first, ...rest] = fromArray;
    return Object.freeze([first as string, ...rest] as const);
  }
  if (opts.relay) return Object.freeze([opts.relay] as const);
  throw new Error(
    'generateConnectUri: at least one of `relay` or `relays` is required',
  );
}

/**
 * Parsed `bunker://<pubkey>?relay=...&secret=...` URI shape.
 *
 * Re-exports nostr-tools' BunkerPointer type for callers that don't
 * want to depend on the upstream module directly.
 */
export type { BunkerPointer };

/**
 * Parse a `bunker://` URI into a BunkerPointer.
 *
 * The expected shape per NIP-46:
 *
 *   bunker://<bunker_pubkey>?relay=<wss-url>&relay=<wss-url-N>&secret=<random>
 *
 * Differences vs nostrconnect://:
 *   - The authority is the BUNKER's pubkey (e.g. clave's signer key),
 *     not the client's ephemeral pubkey.
 *   - The secret is the bunker's challenge we must echo on connect.
 *   - The relays are where we (the client) publish the connect RPC.
 *
 * Returns null if the URI is malformed (missing prefix, invalid pubkey,
 * non-bunker scheme, etc.) or if it advertises zero relays. The caller
 * is expected to surface a friendly error in the UI.
 */
export async function parseBunkerUri(uri: string): Promise<BunkerPointer | null> {
  const trimmed = uri.trim();
  if (!trimmed.startsWith('bunker://')) return null;
  // Delegate the heavy lifting (pubkey hex shape, relay extraction,
  // secret extraction) to nostr-tools so we stay in lockstep with the
  // BunkerSigner.fromBunker consumer.
  let pointer: BunkerPointer | null;
  try {
    pointer = await parseBunkerInput(trimmed);
  } catch {
    return null;
  }
  if (!pointer) return null;
  // NIP-46 requires at least one relay - without it we have no
  // transport for the connect RPC. Reject up-front so the UI can
  // give a precise error instead of a confusing socket timeout
  // 30 seconds later.
  if (!Array.isArray(pointer.relays) || pointer.relays.length === 0) {
    return null;
  }
  return pointer;
}

export interface ConnectBunkerOpts {
  /**
   * One of two pairing modes:
   *   - `connect`: result of {@link generateConnectUri} (QR/nostrconnect flow).
   *   - `bunker`: result of {@link parseBunkerUri} (bunker:// paste flow).
   * Exactly one must be provided.
   */
  connect?: NostrConnectUri;
  bunker?: BunkerPointer;
  /**
   * Abort after this many ms (default 180_000 = 3 minutes). Ignored if
   * {@link ConnectBunkerOpts.signal} is provided.
   */
  timeoutMs?: number;
  /** Optional shared pool. If absent we create a private one. */
  pool?: SimplePool;
  /**
   * Optional AbortSignal for caller-driven cancellation. Lets the UI tear
   * down the listener immediately when the user closes the modal or
   * switches tabs.
   */
  signal?: AbortSignal;
}

/**
 * Wait for a remote signer to complete the NIP-46 handshake.
 *
 * Routing:
 *   - If `opts.connect` is supplied (nostrconnect:// flow), we hand
 *     the URI to {@link BunkerSigner.fromURI}, which publishes a
 *     subscription, waits for the signer to dial back, and resolves
 *     with a ready-to-use BunkerSigner.
 *   - If `opts.bunker` is supplied (bunker:// paste flow), we
 *     construct a new ephemeral client key and hand the pointer to
 *     {@link BunkerSigner.fromBunker}, then call `connect()` to
 *     publish the connect RPC and await the signer's ack.
 *
 * Both paths converge on `wrapBunkerSigner()` for the post-handshake
 * NostrSigner interface.
 */
export async function connectBunker(opts: ConnectBunkerOpts): Promise<AuthResult> {
  if (opts.connect && opts.bunker) {
    throw new BunkerConnectError(
      'connectBunker: pass either `connect` or `bunker`, not both',
    );
  }
  if (!opts.connect && !opts.bunker) {
    throw new BunkerConnectError(
      'connectBunker: one of `connect` or `bunker` is required',
    );
  }
  // Fail-fast on an already-aborted signal so we don't even open a
  // socket. The async path below would eventually catch this too, but
  // skipping the network round-trip keeps cancellation cheap.
  if (opts.signal?.aborted) {
    throw new BunkerTimeoutError();
  }
  const timeoutMs = opts.timeoutMs ?? 180_000;
  const pool = opts.pool ?? new SimplePool();

  // Resolve the relay set we'll need for cleanup-on-error and for
  // wrapping the signer (so disconnect() can close the right pool
  // sockets). For the bunker:// path the relays are baked into the
  // BunkerPointer; for the nostrconnect path they live on the URI.
  const relaysForCleanup: readonly string[] = opts.connect
    ? opts.connect.relays
    : (opts.bunker?.relays ?? []);

  try {
    let bunker: BunkerSigner;
    if (opts.bunker) {
      // bunker:// paste flow - we generate a fresh ephemeral client
      // key (the BunkerPointer doesn't carry one; the signer doesn't
      // know about us yet) and ask BunkerSigner to publish a connect
      // RPC. `connect()` resolves when the signer acks; `getPublicKey`
      // returns the user pubkey the signer manages.
      const clientSk = generateSecretKey();
      bunker = BunkerSigner.fromBunker(clientSk, opts.bunker, { pool });
      // Bridge the caller's AbortSignal to the in-flight connect RPC.
      // BunkerSigner.connect() doesn't accept a signal, so we race it
      // against an abort listener.
      await raceWithAbort(bunker.connect(), opts.signal, timeoutMs);
    } else {
      // nostrconnect:// flow - we already minted the client key in
      // generateConnectUri(); hand the URI directly to fromURI. We race
      // the fromURI promise against our AbortSignal listener so that a
      // caller-driven abort (modal close, tab switch) tears down
      // immediately even if nostr-tools is mid-subscribe and hasn't
      // observed the signal yet.
      const connect = opts.connect!;
      const fromUriArg: AbortSignal | number = opts.signal ?? timeoutMs;
      bunker = await raceWithAbort(
        BunkerSigner.fromURI(
          connect.clientSecretKey,
          connect.uri,
          { pool },
          fromUriArg,
        ),
        opts.signal,
        timeoutMs,
      );
    }

    const userPubkey = await bunker.getPublicKey();
    return {
      pubkey: userPubkey,
      signer: wrapBunkerSigner(bunker, userPubkey, pool, relaysForCleanup),
    };
  } catch (err) {
    try {
      pool.close([...relaysForCleanup]);
    } catch {
      // ignore
    }
    if (err instanceof BunkerTimeoutError) throw err;
    if (err instanceof BunkerConnectError) throw err;
    const msg = err instanceof Error ? err.message : String(err);
    if (
      msg.toLowerCase().includes('timeout') ||
      msg.toLowerCase().includes('abort') ||
      opts.signal?.aborted === true
    ) {
      throw new BunkerTimeoutError();
    }
    throw new BunkerConnectError(msg);
  }
}

/**
 * Race a promise against an AbortSignal and a timeout. Throws
 * BunkerTimeoutError if either fires first. Used to bridge
 * caller-driven cancellation into nostr-tools APIs that don't
 * accept an AbortSignal directly.
 */
async function raceWithAbort<T>(
  p: Promise<T>,
  signal: AbortSignal | undefined,
  timeoutMs: number,
): Promise<T> {
  return await new Promise<T>((resolve, reject) => {
    let settled = false;
    const onAbort = (): void => {
      if (settled) return;
      settled = true;
      reject(new BunkerTimeoutError());
    };
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      reject(new BunkerTimeoutError());
    }, timeoutMs);
    if (signal?.aborted) {
      clearTimeout(timer);
      settled = true;
      reject(new BunkerTimeoutError());
      return;
    }
    signal?.addEventListener('abort', onAbort);
    p.then(
      (v) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
        resolve(v);
      },
      (e) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
        reject(e);
      },
    );
  });
}

function wrapBunkerSigner(
  bunker: BunkerSigner,
  userPubkey: string,
  pool: SimplePool,
  relays: readonly string[],
): NostrSigner {
  return {
    type: 'nip46',
    pubkey: userPubkey,
    async signEvent(event: UnsignedNostrEvent): Promise<SignedNostrEvent> {
      // BunkerSigner.signEvent expects an EventTemplate (kind, created_at,
      // tags, content). Round-trip through the remote signer and re-assert
      // the kind as defence in depth.
      const signed = (await bunker.signEvent({
        kind: event.kind,
        created_at: event.created_at,
        tags: event.tags,
        content: event.content,
      })) as unknown as SignedNostrEvent;
      if (signed.kind !== event.kind) {
        throw new Error(
          `NIP-46 signer returned kind ${signed.kind}, expected ${event.kind}`,
        );
      }
      return signed;
    },
    async disconnect(): Promise<void> {
      try {
        await bunker.close();
      } catch {
        // ignore
      }
      try {
        pool.close([...relays]);
      } catch {
        // ignore
      }
    },
  };
}

/**
 * Build a NIP-42-style kind-22242 challenge event and sign it via the
 * provided signer. The worker uses this to verify identity and mint a
 * sessionToken JWT.
 */
export interface ChallengeInput {
  challenge: string;
  relay: string;
}

export async function signAuthChallenge(
  signer: NostrSigner,
  input: ChallengeInput,
): Promise<SignedNostrEvent> {
  const event: UnsignedNostrEvent = {
    kind: 22242,
    created_at: Math.floor(Date.now() / 1000),
    tags: [
      ['challenge', input.challenge],
      ['relay', input.relay],
    ],
    content: '',
  };
  return await signer.signEvent(event);
}
